import { BodyLarge, FlexAlignCenter, FlexSpaceBetween, Headline3 } from 'components/Elements';
import strings from 'strings/strings.json';
import { ContentColumn } from '../components/shared';
import ActivityCardGrid from '../components/ActivityCardGrid';
import { useContext, useMemo } from 'react';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import { ActivityType } from 'models/Activity';
import TaskIcon from 'assets/icons/pathway/TaskIcon';
import QuestionnaireIcon from 'assets/icons/pathway/QuestionnaireIcon';
import ScheduleIcon from 'assets/icons/navigation/ScheduleIcon';
import Header from 'components/sections/Header';
import LearnerActivityView from '../activity/LearnerActivityView';
import { styled } from 'styled-components';
import DropdownField, { DropdownOption } from 'pages/build-course/components/DropdownField';
import LearnerImmersiveTabs from '../components/navigation/LearnerImmeriveTabs';
import { LearnerTabDefinition } from '../components/navigation/LearnerTabs';
import { sizes, theme } from 'theme';

export default function LearnerImmersiveView(): JSX.Element {
    const { step, setStep } = useContext(LearnerCourseContext);
    const learnerCourse = useContext(LearnerCourseContext).learnerCourse!;

    const module = useMemo(() => {
        if (step.stepType === 'Module') {
            return learnerCourse.modules.find((m) => m.id === step.id);
        }
        if (step.stepType === 'Activity') {
            return learnerCourse.modules.find((m) => m.activities.some((a) => a.activityStatusId === step.id));
        }
        return learnerCourse.modules[0];
    }, [learnerCourse, step]);

    if (!module) return <></>;

    const moduleOverviewTab: LearnerTabDefinition = {
        id: module.id,
        name: `Overview `,
        icon: ScheduleIcon,
        hideCompleteIcon: true,
        action: () => setStep({ id: module.id, stepType: 'Module' }),
        content: (
            <ModuleContentColumn>
                <Headline3>{strings.learnerCoursePage.descriptionTitle}</Headline3>
                {module.description && <BodyLarge>{module.description}</BodyLarge>}
                <ActivityCardGrid />
            </ModuleContentColumn>
        ),
    };

    const activityTabs: LearnerTabDefinition[] = module.activities
        .filter((a) => a.type !== ActivityType.PRESENTATION_SLIDE)
        .map(
            (activity, index): LearnerTabDefinition => ({
                id: activity.activityStatusId,
                name: `Activity ${index + 1} `,
                icon: activity.type === ActivityType.TASK ? TaskIcon : QuestionnaireIcon,
                action: () => setStep({ id: activity.activityStatusId, stepType: 'Activity' }),
                content: <LearnerActivityView />,
                complete: activity.completed,
            }),
        );

    activityTabs.unshift(moduleOverviewTab);

    const moduleOptions: DropdownOption[] = learnerCourse.modules.map((m) => ({
        label: `Module ${m.order}`,
        value: m.id,
    }));

    const moduleDropDownAndCompletionCounter = (
        <DropDownContainer>
            <DropdownField
                id="module-dropdown"
                labelText={strings.learnerCoursePage.selectModuleLabel}
                options={moduleOptions}
                onChange={(e) => setStep({ id: e.target.value, stepType: 'Module' })}
                value={module.id}
            />
            <BackgroundCircle>
                <BodyLarge $bold $nowrap>
                    {module.activities.filter((a) => a.completed).length}/{module.activities.length}
                </BodyLarge>
            </BackgroundCircle>
        </DropDownContainer>
    );

    return (
        <>
            <Header
                aboveTitleElement={moduleDropDownAndCompletionCounter}
                title={module.title}
                maxWidth={sizes.learnerImmersiveContentMaxWidth}
                learnerTabOverlap
            />
            <LearnerImmersiveTabs tabs={activityTabs} />
        </>
    );
}

const DropDownContainer = styled(FlexSpaceBetween)`
    align-items: flex-end;
`;

const ModuleContentColumn = styled(ContentColumn)`
    gap: ${sizes.spacingMd};
`;

const BackgroundCircle = styled(FlexAlignCenter)`
    background: ${theme.textColourInverted};
    border-radius: 50%;
    padding: ${sizes.spacingMd};
    max-width: 64px;
    max-height: 64px;
`;
